import { action, computed, observable, reaction } from "mobx";
import { ObservableQuery } from "@apollo/client";
import { store } from "@store";
import { FormStore } from "@modules/formx";
import {
  ConsultantContractListItem,
  CustomerContractSnapshotListItemFragment,
  CustomerContractSnapshot_Delete,
  CustomerContractSnapshots_ByContractId,
  LegalPersonType,
  CustomerContractSnapshot_UploadSigned,
  CustomerContractSnapshot_Generate,
  CustomerContractSnapshot_Nullify
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { FieldProps } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import * as f from "@consultants/ConsultantContract/fields";
import { getMimeType } from "@bonliva-crm/shared";

import { logSubscriptionError } from "@utils/log-subscription-error";
type FormDataUpload = {
  SignedOn: Date;
};

export const defaultFields = [f.signedOn];

export const initialValues: FormDataUpload = {
  SignedOn: null
};

export class ContractTableStore {
  form: FormStore<FormDataUpload>;

  @observable loading = false;
  @observable loadingGenerate = false;
  @observable open = false;
  @observable missedFields: string[] = [];
  @observable canAddContract = false;
  @observable list: CustomerContractSnapshotListItemFragment[] = null;
  @observable fileToUpload: File = null;
  @observable contractIdToUpload = null;
  @observable contractToUpload: ConsultantContractListItem = null;
  @observable fields: FieldProps[] = [];
  @observable legalPersonType: LegalPersonType = null;
  @observable preFilledData: FormData = null;
  // @observable contractData: ConsultantInfo = null;

  get actualItems() {
    return this.list;
    // todo add later
    // ?.filter(x =>
    // moment(x.EndDate).isAfter(moment().subtract(30, "days"))
    // );
  }

  get pastItems() {
    return [];
    // todo fix
    // return this.list?.filter(x =>
    //   moment(x.EndDate).isBefore(moment().subtract(30, "days"))
    // );
  }

  query: ObservableQuery<
    CustomerContractSnapshots_ByContractId.Return,
    CustomerContractSnapshots_ByContractId.Variables
  >;

  constructor(id: string) {
    this.getList(id);

    this.form = new FormStore<FormDataUpload>({
      onSubmit: this.onSubmit,
      initialValues
    });

    reaction(
      () => this.contractToUpload,
      (e) => {
        if (e) {
          this.fields = [f.signedOn];
        }
      }
    );

    (window as any).customerContractSnapshotTableStore = this;
  }

  subscribe = () => {
    this.query.subscribe({
      next: ({ data }) => {
        this.list = data.getCustomerContractSnapshots;
        this.loading = false;
      },
      error: logSubscriptionError("SnapshotsTableStore")
    });
  };

  @action getList = (customerContractId) => {
    this.loading = true;
    this.query = CustomerContractSnapshots_ByContractId.funcWatch({
      customerContractId
    });
    this.subscribe();
  };

  @action requestUpload = () =>
    store()
      .upload.requestFileUpload({
        multiple: false,
        acceptMimeTypes: getMimeType("pdf")
      })
      .then((files) => {
        this.fileToUpload = files[0];
      });

  onSubmit = async (cb?) => {
    const { valid, values } = this.form.state;
    if (valid && this.fileToUpload) {
      this.loading = true;
      try {
        await CustomerContractSnapshot_UploadSigned.func(
          {
            snapshotId: this.contractIdToUpload,
            file: this.fileToUpload,
            signedOn: values.SignedOn
          },
          { refetchQueries: [CustomerContractSnapshots_ByContractId.OperationName] }
        ).then((res) => {
          store().snackbars.success(locale.UploadSignedDocument);
          this.form.reset(initialValues);
          this.fileToUpload = null;
          this.contractIdToUpload = null;
          this.contractToUpload = null;
          cb(null);
        });
      } catch (e) {
        store().snackbars.error(`${e}`);
      } finally {
        this.loading = false;
        cb(null);
      }
    }
  };

  @action resetUpload = () => (this.fileToUpload = null);

  @computed get isAdmin() {
    return store().user.hasPermission("CanDeleteConsultantContract");
  }

  @action deleteFromList = (snapshotId) => {
    CustomerContractSnapshot_Delete.func({ snapshotId })
      .then(() => (this.list = this.list.filter((el) => el._id !== snapshotId)))
      .catch((e) => store().snackbars.error(`${e}`));
  };

  @action handleGenerate = (snapshotId) => {
    this.loadingGenerate = true;
    CustomerContractSnapshot_Generate.func(
      { snapshotId },
      { refetchQueries: [CustomerContractSnapshots_ByContractId.OperationName] }
    )
      .finally(() => (this.loadingGenerate = false))
      .catch((e) => store().snackbars.error(`${e}`));
  };

  @action handleNullify = (value: boolean, snapshotId: string) => {
    CustomerContractSnapshot_Nullify.func(
      { snapshotId, value },
      { refetchQueries: [CustomerContractSnapshots_ByContractId.OperationName] }
    ).catch((e) => store().snackbars.error(`${e}`));
  };

  download = (link: string) => {
    const a = document.createElement("a");
    a.href = link;
    a.click();
  };
}
